/**
 * @pit/sim — precise integer facing DIRECTION (#62).
 *
 * ──────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS
 *
 * Facing used to be stored in sim state as a 16-way sector integer
 * (`Facing = 0..15`). That baked RENDER granularity into the AUTHORITATIVE
 * simulation. This module makes facing a precise integer direction vector in
 * the sim; the 16-sector value becomes a pure render-time derivation
 * (`sectorFromDelta` in `facing.ts`), never stored in `SimState`.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * REPRESENTATION — canonical reduced integer vector
 *
 * A `FacingDir` is a branded `{ fx, fy }` of integers in **canonical form**:
 * the direction reduced to lowest terms via integer gcd, so `(3,0)`, `(6,0)`
 * and `(100,0)` all canonicalise to `(1,0)` — a direction has ONE identity.
 * This is the invariant that keeps the hash stable: same direction ⇒ same
 * stored vector ⇒ same hash.
 *
 * DETERMINISM (the core constraint):
 *   • No normalization, no sqrt, no float — canonicalisation is integer gcd
 *     ONLY. Platform-identical, exact.
 *   • Direction comparisons (frontal arc, mirror) are integer dot/cross
 *     products — exact, cheap (see `facingDot` / `facingCross`).
 *
 * ZERO-VECTOR is unrepresentable: `facingDirFromDelta(0,0)` returns `null`;
 * callers supply a fallback (mirroring the old `face ?? caster.facing`). A
 * `FacingDir` value is therefore ALWAYS non-zero by construction.
 */

import { idiv, imod, isqrt } from "@pit/math";
import type { FacingDir } from "./types.js";

/** Integer absolute value (Math.* is banned in sim src). */
function iabs(n: number): number {
  return n < 0 ? -n : n;
}

/**
 * Canonical North direction `(0,-1)` (screen-Y: negative dy is North). The
 * deterministic fallback when a delta is zero (mirrors the old `FACING.N`
 * default). Already in lowest terms + non-zero, so it's a valid `FacingDir`.
 */
export const FACING_DIR_N: FacingDir = { fx: 0, fy: -1 } as unknown as FacingDir;

/**
 * Greatest common divisor of two non-negative integers (Euclid). Integer-only,
 * total, terminating. `gcdNonNeg(0, 0) === 0` (only reached when both inputs
 * are zero, which the caller filters out before relying on the result).
 */
function gcdNonNeg(a: number, b: number): number {
  let x = a;
  let y = b;
  while (y !== 0) {
    // Inputs are non-negative (caller `iabs`es), so floored `imod` == the
    // truncated remainder Euclid needs. No raw `%` (banned in sim).
    const t = imod(x, y);
    x = y;
    y = t;
  }
  return x;
}

/**
 * The documented `as` cast for this module's brand. `FacingDir` is nominal;
 * the runtime value is a plain `{fx,fy}` of integers already in canonical
 * form by construction (only `facingDirFromDelta` / `facingDirFromComponents`
 * produce it).
 */
function brandFacingDir(fx: number, fy: number): FacingDir {
  return { fx, fy } as unknown as FacingDir;
}

/**
 * Construct a canonical `FacingDir` from a raw displacement `(dx, dy)`.
 * Returns `null` for the zero vector (no direction). Reduces to lowest terms
 * via gcd, preserving sign — the SMART CONSTRUCTOR: the only way to mint a
 * `FacingDir`, so every value in the system is canonical + non-zero.
 *
 * Pure. Integer-only. No float, no sqrt, no division beyond integer `%`/`/`
 * by the gcd (exact — gcd divides both components).
 */
export function facingDirFromDelta(dx: number, dy: number): FacingDir | null {
  if (dx === 0 && dy === 0) return null;
  const g = gcdNonNeg(iabs(dx), iabs(dy));
  // g >= 1 here (not both zero) and divides both components exactly, so
  // floored `idiv` is exact (no rounding) and sign-preserving. No raw `/`.
  return brandFacingDir(idiv(dx, g), idiv(dy, g));
}

/**
 * Canonical `FacingDir` from origin `(ox,oy)` toward target `(tx,ty)`.
 * Returns `null` iff origin === target. Convenience wrapper — the direct
 * replacement for the old `facingToward`.
 */
export function facingDirToward(
  ox: number,
  oy: number,
  tx: number,
  ty: number,
): FacingDir | null {
  return facingDirFromDelta(tx - ox, ty - oy);
}

/** The raw integer components of a facing (for hashing, wire derivation). */
export function facingComponents(dir: FacingDir): { readonly fx: number; readonly fy: number } {
  return { fx: dir.fx, fy: dir.fy };
}

/**
 * Integer dot product of a facing with a displacement `(dx,dy)`. Sign tells
 * you the half-plane: `> 0` ⇒ the point is in FRONT of the facing, `< 0`
 * ⇒ behind, `0` ⇒ exactly perpendicular. Exact; the basis for frontal-arc
 * and "is target in front" checks (#27) without any snapping.
 */
export function facingDot(dir: FacingDir, dx: number, dy: number): number {
  const c = facingComponents(dir);
  return c.fx * dx + c.fy * dy;
}

/**
 * Integer 2D cross product (z-component) of a facing with a displacement.
 * Sign tells you the turn side (left/right of facing); `0` ⇒ colinear.
 * Exact; the basis for arc-half and mirror-side decisions.
 */
export function facingCross(dir: FacingDir, dx: number, dy: number): number {
  const c = facingComponents(dir);
  return c.fx * dy - c.fy * dx;
}

/**
 * THE shared bp-cosine ARC predicate (#27 facing, #92 positional, #128 cone).
 * Every arc test in the sim funnels through here so cone selection, the
 * caster-facing gate, and the target-relative positional gate can NEVER
 * disagree about "is this offset inside the arc".
 *
 * Inputs are the two exact-integer projections of an offset vector onto a
 * reference AXIS frame:
 *   • `along` — the component ALONG the axis (a dot product; sign = which
 *               half-plane of the axis the offset is in).
 *   • `perp`  — the component PERPENDICULAR to the axis (a cross product; only
 *               its magnitude matters, so its sign is irrelevant here).
 *
 * The angle θ between the offset and the axis satisfies
 * `cos θ = along / (|axis|·|offset|)` and `|axis|·|offset| = isqrt(along² + perp²)`.
 * The arc has half-angle α with `cos α = cosThresholdBp / 10000`. "Within arc"
 * is `θ ≤ α`, i.e. `cos θ ≥ cos α`, i.e.
 *
 *     10000·along ≥ cosThresholdBp · isqrt(along² + perp²)
 *
 * which holds for ALL signs of `along`/`cosThresholdBp` (no squaring ⇒ no sign
 * split, no overflow). Integer-only + deterministic: `isqrt` is the floored
 * integer length, platform-identical. Under the arena bound (|coord| ≤
 * MAX_UNITS) `along² + perp²` stays inside the JS safe-integer range (the same
 * regime `distSq` relies on). Boundary (exact equality) is INCLUSIVE — the arc
 * edge counts as inside (matching #27/#92).
 */
export function arcContains(along: number, perp: number, cosThresholdBp: number): boolean {
  const len = isqrt(along * along + perp * perp); // = |axis|·|offset|, floored
  return 10000 * along >= cosThresholdBp * len;
}

/**
 * #27: is the caster (at `casterX,casterY`, facing `facing`) facing the point
 * `targetX,targetY` within the arc whose half-angle α has cosine
 * `cosThresholdBp / 10000`? Reads the CASTER's facing against the offset toward
 * the target via `arcContains` (`along = dot`, `perp = cross`). Integer-only,
 * deterministic (`isqrt`, no trig). A coincident target (`off = 0`) has no
 * direction and is trivially satisfied — you cannot fail to face a unit standing
 * on your own tile. Boundary (exact equality) counts as facing (arc edge
 * inclusive). Shared by the #27 cast facing gate AND the #93 auto-attack swing
 * gate so the two can never disagree about "is the target in my arc".
 */
export function facingSatisfied(
  facing: FacingDir,
  casterX: number,
  casterY: number,
  targetX: number,
  targetY: number,
  cosThresholdBp: number,
): boolean {
  const dx = targetX - casterX;
  const dy = targetY - casterY;
  if (dx === 0 && dy === 0) return true; // coincident — no direction to gate
  const dot = facingDot(facing, dx, dy);
  const cross = facingCross(facing, dx, dy);
  return arcContains(dot, cross, cosThresholdBp);
}
